import { useState, useEffect, FormEvent } from "react";
import { AccountInfo } from "@efwwit/integration-hub-sdk/dist/nodejs/types/identity";
import { Check, Save, MoreHoriz, Report } from "@mui/icons-material";
import { Grid, Button, FormControl, FormLabel, Textarea, Chip, Box, Alert } from "@mui/joy";

import { useGlobalContext } from "../../Context";

type CommentsProps = {
  profile: AccountInfo;
};

enum SaveStatus {
  loading = "loading",
  save = "save",
  saved = "saved",
  saving = "saving",
}

const SaveStatusIcon = ({ status }: { status: SaveStatus }) => {
  switch (status) {
    case SaveStatus.saving:
    case SaveStatus.loading:
      return <MoreHoriz />;
    case SaveStatus.saved:
      return <Check />;
    default:
      return <Save />;
  }
};

type ExtendedAttrs = { id: string; travel?: { comments: string } };

export default function Comments({ profile }: CommentsProps) {
  const { core } = useGlobalContext();
  const [saveCommentsStatus, setSaveCommentsStatus] = useState<SaveStatus>(SaveStatus.loading);
  const [comments, setComments] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (core && profile) {
      core
        .invokeIntegrationHubRequest("GET", `/identity/users/${profile.id}/extended-attributes`, undefined, {
          "Cache-Control": "no-cache",
        })
        .then((response) => {
          if (response.statusCode === 200) {
            const { travel } = response.body as ExtendedAttrs;
            if (travel?.comments) setComments(travel.comments);
            setSaveCommentsStatus(SaveStatus.save);
          } else {
            console.error(response);
            setError(true);
          }
        });
    }
  }, [core, profile]);

  useEffect(() => {
    if (saveCommentsStatus === SaveStatus.saved) {
      setTimeout(() => {
        setSaveCommentsStatus(SaveStatus.save);
      }, 2000);
    }
  }, [saveCommentsStatus]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (saveCommentsStatus === SaveStatus.save && core && profile) {
      setSaveCommentsStatus(SaveStatus.saving);

      const response = await core
        .invokeIntegrationHubRequest("POST", `/identity/users/${profile.id}/extended-attributes`, {
          app: "travel",
          data: { comments },
        })
        .catch((e) => {
          console.error(e);
          setError(true);
        });

      if (response && response.statusCode === 200) {
        setSaveCommentsStatus(SaveStatus.saved);
      } else {
        setError(true);
        setSaveCommentsStatus(SaveStatus.save);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box
        sx={{
          display: { xs: "grid", sm: "grid" },
          flexWrap: "wrap",
          gap: 2,
          pb: 3,
        }}
      >
        <Grid container direction="row-reverse">
          <Grid>
            <Button
              sx={{ textTransform: "capitalize" }}
              type="submit"
              startDecorator={<SaveStatusIcon status={saveCommentsStatus} />}
              disabled={saveCommentsStatus !== SaveStatus.save}
            >
              {saveCommentsStatus}
            </Button>
          </Grid>
        </Grid>
        <Grid sx={{ display: error ? "block" : "none" }}>
          <Alert startDecorator={<Report />} color="danger">
            Something went wrong while trying to Save the comment. Please try again later.
          </Alert>
        </Grid>
        <Grid>
          <FormControl sx={{ flex: 1, minWidth: { xs: "100%", sm: "49%" } }}>
            <FormLabel sx={{ display: { sm: "none", xs: "flex" } }}>{"Comments"}</FormLabel>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              name="comments"
              minRows={4}
              startDecorator={<Chip sx={{ display: { sm: "flex", xs: "none" } }}>{"Comments"}</Chip>}
            ></Textarea>
          </FormControl>
        </Grid>
      </Box>
    </form>
  );
}
